import type { DtoolsStagingLine, DtoolsStagingSession } from "./dtoolsJsonStaging";
import { DTOOLS_JSON_SOURCE } from "./dtoolsSiJsonImport";
import type { DtoolsLineCatalogMeta } from "./dtoolsCatalogueMatch";
import {
  mergeDtoolsLineCatalogMeta,
  parseDtoolsLineCatalogMeta,
  resolveEffectiveCatalogMatchConfidence,
} from "./dtoolsCatalogueMatch";
import type { CommercialParsedRowAuditEntry } from "./proposalCommercialInterpretation";
import { utf8ByteLength, utf8JsonByteSize } from "./utf8ByteLength";

export type DtoolsPortalPaymentTerm = { stage: string; percentage: number };

export const DTOOLS_SAFE_DESCRIPTION_MAX_LEN = 500;
export const DTOOLS_SAFE_ITEM_RAW_TEXT_MAX_LEN = 2000;
export const DTOOLS_SAFE_HEADER_TEXT_MAX_LEN = 2000;
/** Guard parsedMetadataJson before proposal_headers insert (TiDB/MySQL packet safety). */
export const DTOOLS_SAFE_PARSED_METADATA_MAX_BYTES = 350_000;

export const DTOOLS_SAVE_TOO_LARGE_USER_MESSAGE =
  "Proposal could not be saved because the imported D-Tools file is too large. We saved the parsed proposal details, but the raw import needs to remain in staging. Please try again or contact admin.";

export function trimDtoolsText(value: string | null | undefined, maxLen: number): string {
  const text = String(value ?? "").trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, Math.max(0, maxLen - 1))}…`;
}

export function approximateJsonByteSize(value: unknown): number {
  return utf8JsonByteSize(value);
}

export function buildDtoolsPersistedItemRawText(
  line: DtoolsStagingLine & Partial<DtoolsLineCatalogMeta>,
): string {
  const payload: Record<string, unknown> = {
    source: DTOOLS_JSON_SOURCE,
    dtoolsItemId: line.dtoolsItemId,
    catalogueKey: line.catalogueKey,
    catalogId: line.catalogId,
    componentId: line.componentId,
    bucket: line.bucket,
    systemId: line.systemId,
    systemName: line.systemName,
    locationId: line.locationId,
    roomName: line.roomName,
    locationName: line.locationName ?? null,
    locationFullName: line.locationFullName ?? null,
    parentLocationId: line.parentLocationId ?? null,
    parentId: line.parentId,
    isAccessory: line.isAccessory,
    originalDtoolsDescription: trimDtoolsText(line.dtoolsDescription, DTOOLS_SAFE_DESCRIPTION_MAX_LEN),
    polishedDescription: trimDtoolsText(line.polishedDescription, DTOOLS_SAFE_DESCRIPTION_MAX_LEN),
  };
  if (line.catalogProductId != null && line.catalogProductId > 0) {
    payload.catalogProductId = line.catalogProductId;
  }
  if (line.projectCatalogueId != null && line.projectCatalogueId > 0) {
    payload.projectCatalogueId = line.projectCatalogueId;
  }
  const effectiveConfidence = resolveEffectiveCatalogMatchConfidence({
    catalogMatchConfidence: line.catalogMatchConfidence,
    catalogProductId: line.catalogProductId,
    projectCatalogueId: line.projectCatalogueId,
  });
  if (effectiveConfidence !== "none") payload.catalogMatchConfidence = effectiveConfidence;
  const resolvedImage = line.proposalImageOverride?.trim() || line.imageUrl?.trim();
  if (resolvedImage) {
    payload.proposalImageOverride = trimDtoolsText(resolvedImage, 512);
  }
  if (line.proposalDescriptionOverride?.trim()) {
    payload.proposalDescriptionOverride = trimDtoolsText(
      line.proposalDescriptionOverride,
      DTOOLS_SAFE_DESCRIPTION_MAX_LEN,
    );
  }
  if (line.baseUnitPrice != null && Number.isFinite(Number(line.baseUnitPrice))) {
    payload.baseUnitPrice = Math.round(Number(line.baseUnitPrice) * 100) / 100;
  }
  if (line.pricingMethod) payload.pricingMethod = line.pricingMethod;
  if (line.percentageOfEquipment != null && Number.isFinite(Number(line.percentageOfEquipment))) {
    payload.percentageOfEquipment = Number(line.percentageOfEquipment);
  }
  if (line.clarityServiceUserOverride === true) payload.clarityServiceUserOverride = true;
  const serialized = JSON.stringify(payload);
  if (serialized.length <= DTOOLS_SAFE_ITEM_RAW_TEXT_MAX_LEN) return serialized;
  return serialized.slice(0, DTOOLS_SAFE_ITEM_RAW_TEXT_MAX_LEN);
}

export function trimDtoolsPersistedItemRawTextFromUnknown(rawText: string | null | undefined): string {
  const text = String(rawText ?? "").trim();
  if (!text) return "";
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (parsed.source !== DTOOLS_JSON_SOURCE) {
      return trimDtoolsText(text, DTOOLS_SAFE_ITEM_RAW_TEXT_MAX_LEN);
    }
    const catalogMeta = parseDtoolsLineCatalogMeta(parsed);
    const compact: Record<string, unknown> = {
      source: DTOOLS_JSON_SOURCE,
      dtoolsItemId: parsed.dtoolsItemId ?? null,
      catalogueKey: parsed.catalogueKey ?? null,
      catalogId: parsed.catalogId ?? null,
      componentId: parsed.componentId ?? null,
      bucket: parsed.bucket ?? null,
      systemId: parsed.systemId ?? null,
      systemName: parsed.systemName ?? null,
      locationId: parsed.locationId ?? null,
      roomName: parsed.roomName ?? null,
      locationName: parsed.locationName ?? null,
      locationFullName: parsed.locationFullName ?? null,
      parentLocationId: parsed.parentLocationId ?? null,
      parentId: parsed.parentId ?? null,
      isAccessory: parsed.isAccessory ?? false,
      originalDtoolsDescription: trimDtoolsText(
        String(parsed.originalDtoolsDescription ?? ""),
        DTOOLS_SAFE_DESCRIPTION_MAX_LEN,
      ),
      polishedDescription: trimDtoolsText(String(parsed.polishedDescription ?? ""), DTOOLS_SAFE_DESCRIPTION_MAX_LEN),
      ...mergeDtoolsLineCatalogMeta({}, catalogMeta),
    };
    const projectCatalogueId = Number(parsed.projectCatalogueId);
    if (Number.isFinite(projectCatalogueId) && projectCatalogueId > 0) {
      compact.projectCatalogueId = projectCatalogueId;
    }
    const serialized = JSON.stringify(compact);
    if (serialized.length <= DTOOLS_SAFE_ITEM_RAW_TEXT_MAX_LEN) return serialized;
    return serialized.slice(0, DTOOLS_SAFE_ITEM_RAW_TEXT_MAX_LEN);
  } catch {
    return trimDtoolsText(text, DTOOLS_SAFE_ITEM_RAW_TEXT_MAX_LEN);
  }
}

export type SafeDtoolsHeaderTextInput = {
  proposalName: string;
  projectNumber: string;
  sessionId: string;
  fileName?: string | null;
  itemCount: number;
  parserVersion?: string | null;
  rawJsonByteSize?: number | null;
};

export function buildSafeDtoolsHeaderExtractedText(input: SafeDtoolsHeaderTextInput): string {
  const summary = [
    "D-Tools JSON import (raw JSON stored in staging only).",
    `Proposal: ${trimDtoolsText(input.proposalName, 120)}`,
    `Ref: ${trimDtoolsText(input.projectNumber, 64)}`,
    `Session: ${trimDtoolsText(input.sessionId, 128)}`,
    `File: ${trimDtoolsText(input.fileName ?? "upload", 120)}`,
    `Lines: ${Math.max(0, Math.round(input.itemCount))}`,
    `Parser: ${trimDtoolsText(input.parserVersion ?? "unknown", 64)}`,
    input.rawJsonByteSize != null && input.rawJsonByteSize > 0
      ? `Source JSON size: ${input.rawJsonByteSize} bytes (not persisted on proposal header).`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return trimDtoolsText(summary, DTOOLS_SAFE_HEADER_TEXT_MAX_LEN);
}

export function buildSafeDtoolsParsedText(input: SafeDtoolsHeaderTextInput): string {
  return trimDtoolsText(
    `D-Tools SI JSON import (${input.fileName ?? "upload"}) · ${input.itemCount} line(s) · session ${input.sessionId}`,
    DTOOLS_SAFE_HEADER_TEXT_MAX_LEN,
  );
}

export function summarizeDtoolsItemRawText(rawText: string | null | undefined): Record<string, unknown> | null {
  const text = String(rawText ?? "").trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (parsed.source !== DTOOLS_JSON_SOURCE) return null;
    return {
      dtoolsItemId: parsed.dtoolsItemId ?? null,
      catalogueKey: parsed.catalogueKey ?? null,
      systemName: parsed.systemName ?? null,
      roomName: parsed.roomName ?? null,
      locationName: parsed.locationName ?? null,
      locationFullName: parsed.locationFullName ?? null,
      parentLocationId: parsed.parentLocationId ?? null,
      parentId: parsed.parentId ?? null,
      isAccessory: Boolean(parsed.isAccessory),
      originalDtoolsDescription: trimDtoolsText(
        String(parsed.originalDtoolsDescription ?? ""),
        DTOOLS_SAFE_DESCRIPTION_MAX_LEN,
      ),
      polishedDescription: trimDtoolsText(String(parsed.polishedDescription ?? ""), DTOOLS_SAFE_DESCRIPTION_MAX_LEN),
      ...parseDtoolsLineCatalogMeta(parsed),
    };
  } catch {
    return null;
  }
}

export function trimDtoolsCommercialParseAuditEntry(
  entry: CommercialParsedRowAuditEntry,
): Record<string, unknown> {
  const summary = summarizeDtoolsItemRawText(entry.rawText);
  const { rawText: _rawText, ...rest } = entry;
  return {
    ...rest,
    description: trimDtoolsText(entry.description, DTOOLS_SAFE_DESCRIPTION_MAX_LEN),
    ...(summary ? { dtoolsItemSummary: summary } : {}),
  };
}

export function trimCommercialParseAuditForDtools(audit: unknown[]): unknown[] {
  if (!Array.isArray(audit)) return [];
  return audit.map((row) => trimDtoolsCommercialParseAuditEntry(row as CommercialParsedRowAuditEntry));
}

export function buildDtoolsImportSummary(
  session: DtoolsStagingSession,
  itemCount: number,
): Record<string, unknown> {
  const { parseResult: result } = session;
  const rawJsonByteSize = utf8ByteLength(String(session.rawJson ?? ""));
  return {
    dtoolsImport: true,
    dtoolsImportSummary: {
      sessionId: session.sessionId,
      fileName: session.fileName ?? null,
      proposalRef: result.project.proposalRef ?? null,
      proposalName: result.project.proposalName,
      parserVersion: result.version,
      source: DTOOLS_JSON_SOURCE,
      itemCount,
      sourceItemCount: result.itemCount ?? itemCount,
      rawJsonByteSize,
      rawJsonPersistedOnHeader: false,
      importedAt: session.createdAt,
    },
  };
}

export function buildSafeDtoolsProposalMetadata(
  session: DtoolsStagingSession,
  options: {
    itemCount: number;
    hierarchySnapshot: Record<string, unknown>;
    paymentTerms: DtoolsPortalPaymentTerm[];
    totalsCheck?: Record<string, unknown> | null;
    parseWarnings?: string[];
    reviewRequired?: boolean;
    blockReasons?: string[];
    customerId?: number | null;
  },
): Record<string, unknown> {
  const { parseResult: result } = session;
  return {
    ...buildDtoolsImportSummary(session, options.itemCount),
    dtoolsStagingSessionId: session.sessionId,
    dtoolsHierarchySnapshot: options.hierarchySnapshot,
    dtoolsPaymentTerms: options.paymentTerms,
    totalsCheck: options.totalsCheck ?? null,
    parserStrategy: DTOOLS_JSON_SOURCE,
    parserVersion: result.version,
    reviewRequired: options.reviewRequired ?? false,
    blockReasons: options.blockReasons ?? [],
    parseWarningsFull: (options.parseWarnings ?? []).slice(0, 24),
    customerId: options.customerId ?? null,
    proposalName: result.project.proposalName,
    projectNumber: result.project.proposalRef ?? null,
    customerName: result.customer?.clientName ?? null,
    address: result.customer?.siteAddress ?? null,
    grandTotal: Number(result.project.projectTotal ?? 0),
    inferredClassification: "project",
  };
}

export function enforceSafeParsedMetadataJson(
  metadata: Record<string, unknown>,
  maxBytes: number = DTOOLS_SAFE_PARSED_METADATA_MAX_BYTES,
): Record<string, unknown> {
  let current: Record<string, unknown> = { ...metadata };
  if (approximateJsonByteSize(current) <= maxBytes) return current;

  const trimSteps: Array<(meta: Record<string, unknown>) => Record<string, unknown>> = [
    (meta) => {
      if (!meta.dtoolsImport && !meta.dtoolsHierarchySnapshot) return meta;
      const next = { ...meta };
      delete next.commercialParseAudit;
      delete next.extractedRowsAudit;
      next.dtoolsCommercialAuditOmitted = true;
      return next;
    },
    (meta) => {
      const next = { ...meta };
      delete next.extractedRowsAudit;
      return next;
    },
    (meta) => {
      const audit = meta.commercialParseAudit;
      if (!Array.isArray(audit)) return meta;
      return {
        ...meta,
        commercialParseAudit: audit.map((row) => {
          const entry = row as Record<string, unknown>;
          const { rawText: _raw, description, ...rest } = entry;
          return {
            ...rest,
            description: trimDtoolsText(String(description ?? ""), 200),
            dtoolsItemSummary: entry.dtoolsItemSummary ?? summarizeDtoolsItemRawText(String(entry.rawText ?? "")),
          };
        }),
      };
    },
    (meta) => {
      const next = { ...meta };
      delete next.parseWarningsFull;
      delete next.filterStats;
      delete next.strategyAttempted;
      return next;
    },
    (meta) => {
      const audit = meta.commercialParseAudit;
      if (!Array.isArray(audit) || audit.length <= 40) return meta;
      return {
        ...meta,
        commercialParseAudit: audit.slice(0, 40),
        commercialParseAuditTruncated: true,
        commercialParseAuditOriginalCount: audit.length,
      };
    },
    (meta) => {
      const snap = meta.dtoolsHierarchySnapshot;
      if (!snap || typeof snap !== "object" || Array.isArray(snap)) return meta;
      const snapRecord = snap as Record<string, unknown>;
      if (snapRecord.compact === true || !Array.isArray(snapRecord.systems)) return meta;
      const { systems: _systems, ...rest } = snapRecord;
      return {
        ...meta,
        dtoolsHierarchySnapshot: { ...rest, compact: true },
      };
    },
    (meta) => {
      const snap = meta.dtoolsHierarchySnapshot;
      if (!snap || typeof snap !== "object" || Array.isArray(snap)) return meta;
      const next = { ...meta };
      delete next.commercialParseAudit;
      return next;
    },
  ];

  for (const step of trimSteps) {
    current = step(current);
    if (approximateJsonByteSize(current) <= maxBytes) return current;
  }

  return current;
}

export function isProposalHeaderPayloadSizeError(error: unknown): boolean {
  const parts = [
    (error as { message?: string })?.message,
    (error as { cause?: { message?: string } })?.cause?.message,
    String(error ?? ""),
  ];
  const msg = parts.filter(Boolean).join(" ");
  return /data too long|packet|max_allowed_packet|ER_DATA_TOO_LONG|1406|Got a packet bigger|JSON document/i.test(msg);
}

export function sanitizeDtoolsConfirmUploadErrorMessage(error: unknown): string | null {
  if (!isProposalHeaderPayloadSizeError(error)) return null;
  return DTOOLS_SAVE_TOO_LARGE_USER_MESSAGE;
}

export type DtoolsPersistenceSizeBreakdown = {
  proposalItemsBytes: number;
  proposalItemsCount: number;
  hierarchySnapshotBytes: number;
  optionContentBytes: number;
  commercialAuditBytes: number;
  rawImportBytes: number;
  displaySettingsBytes: number;
  presentationBytes: number;
  otherMetadataBytes: number;
  parsedMetadataJsonBytes: number;
  rawExtractedTextBytes: number;
  parsedTextBytes: number;
  totalBytes: number;
  largestContributor: string;
  exceedsLimit: boolean;
  limitBytes: number;
};

function kb(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function auditDtoolsPersistenceSize(input: {
  proposalItems?: Array<Record<string, unknown>>;
  parsedMetadataJson: Record<string, unknown>;
  rawExtractedText?: string | null;
  parsedText?: string | null;
  limitBytes?: number;
}): DtoolsPersistenceSizeBreakdown {
  const limitBytes = input.limitBytes ?? DTOOLS_SAFE_PARSED_METADATA_MAX_BYTES;
  const meta = input.parsedMetadataJson;
  const hierarchySnapshotBytes = approximateJsonByteSize(meta.dtoolsHierarchySnapshot);
  const commercialAuditBytes = approximateJsonByteSize(meta.commercialParseAudit);
  const optionContentBytes = approximateJsonByteSize(meta.dtoolsSystemContent);
  const displaySettingsBytes = approximateJsonByteSize(meta.dtoolsDisplaySettings);
  const presentationBytes = approximateJsonByteSize(meta.dtoolsProposalPresentation);
  const importSummaryBytes = approximateJsonByteSize(meta.dtoolsImportSummary);
  const parsedMetadataJsonBytes = approximateJsonByteSize(meta);
  const rawExtractedTextBytes = utf8ByteLength(String(input.rawExtractedText ?? ""));
  const parsedTextBytes = utf8ByteLength(String(input.parsedText ?? ""));

  const proposalItemsBytes = (input.proposalItems ?? []).reduce((sum, item) => {
    return (
      sum +
      utf8ByteLength(String(item.rawText ?? "")) +
      utf8ByteLength(String(item.descriptionSnapshot ?? item.description ?? "")) +
      utf8ByteLength(String(item.cleoDescription ?? "")) +
      utf8ByteLength(String(item.customerExplanation ?? "")) +
      utf8ByteLength(String(item.imageUrlSnapshot ?? ""))
    );
  }, 0);

  const rawImportBytes = importSummaryBytes;
  const otherMetadataBytes = Math.max(
    0,
    parsedMetadataJsonBytes -
      hierarchySnapshotBytes -
      commercialAuditBytes -
      optionContentBytes -
      displaySettingsBytes -
      presentationBytes -
      importSummaryBytes,
  );

  const contributors: Array<[string, number]> = [
    ["proposal_items", proposalItemsBytes],
    ["hierarchy snapshot", hierarchySnapshotBytes],
    ["commercial audit", commercialAuditBytes],
    ["option content", optionContentBytes],
    ["display settings", displaySettingsBytes],
    ["presentation", presentationBytes],
    ["raw import summary", rawImportBytes],
    ["other metadata", otherMetadataBytes],
  ];
  contributors.sort((a, b) => b[1] - a[1]);
  const largestContributor = contributors[0]?.[0] ?? "unknown";

  const totalBytes =
    parsedMetadataJsonBytes + proposalItemsBytes + rawExtractedTextBytes + parsedTextBytes;

  return {
    proposalItemsBytes,
    proposalItemsCount: input.proposalItems?.length ?? 0,
    hierarchySnapshotBytes,
    optionContentBytes,
    commercialAuditBytes,
    rawImportBytes,
    displaySettingsBytes,
    presentationBytes,
    otherMetadataBytes,
    parsedMetadataJsonBytes,
    rawExtractedTextBytes,
    parsedTextBytes,
    totalBytes,
    largestContributor,
    exceedsLimit: parsedMetadataJsonBytes > limitBytes,
    limitBytes,
  };
}

export function formatDtoolsPersistenceSizeReport(audit: DtoolsPersistenceSizeBreakdown): string {
  const lines = [
    "Proposal persistence size",
    `- proposal_items: ${kb(audit.proposalItemsBytes)} (${audit.proposalItemsCount} lines)`,
    `- hierarchy snapshot: ${kb(audit.hierarchySnapshotBytes)}`,
    `- option content: ${kb(audit.optionContentBytes)}`,
    `- commercial audit: ${kb(audit.commercialAuditBytes)}`,
    `- presentation: ${kb(audit.presentationBytes)}`,
    `- display settings: ${kb(audit.displaySettingsBytes)}`,
    `- raw import summary: ${kb(audit.rawImportBytes)}`,
    `- parsedMetadataJson total: ${kb(audit.parsedMetadataJsonBytes)} (limit ${kb(audit.limitBytes)})`,
    `- header text fields: ${kb(audit.rawExtractedTextBytes + audit.parsedTextBytes)}`,
    `Total (header + items): ${kb(audit.totalBytes)}`,
    `Largest contributor: ${audit.largestContributor}`,
  ];
  if (audit.exceedsLimit) lines.push("⚠ parsedMetadataJson exceeds safe limit");
  return lines.join("\n");
}
